import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { 
  analyzeTransactions,
  Transaction,
  TransactionStats 
} from '../api/transactions';
import { API_CONFIG } from '../api/config';

const INITIAL_STATS: TransactionStats = {
  totalVolume: 0,
  avgAmount: 0,
  userTxCount: 0,
  rewardDistribution: {
    masternodes: 0,
    staking: 0,
    devFund: 0
  },
  volumeHistory: [],
};

// Interface for data coming from Supabase 'recent_transactions' table
interface DbRecentTransaction {
  txid: string;
  blockindex: number | null;
  blockhash: string | null;
  recipients: number | null;
  amount: number | null;
  timestamp: string | null; // ISO 8601 string
}

// Helper to transform DB transaction to the Transaction type expected by analyzeTransactions
const transformDbTxToTransaction = (dbTx: DbRecentTransaction): Transaction => {
  return {
    txid: dbTx.txid,
    blockindex: dbTx.blockindex ?? 0,
    blockhash: dbTx.blockhash ?? '',
    recipients: dbTx.recipients ?? 0,
    amount: dbTx.amount ?? 0,
    timestamp: dbTx.timestamp ? Math.floor(new Date(dbTx.timestamp).getTime() / 1000) : 0, // Convert ISO to Unix epoch seconds
  };
};

const TRANSACTIONS_LIMIT = 100; // How many transactions to fetch for analysis

export function useTransactionActivity() {
  const [stats, setStats] = useState<TransactionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    async function fetchAndAnalyzeTransactions() {
      if (!mounted) return;
      setLoading(true);
      setError(null);
      try {
        const { data: dbTxs, error: dbError } = await supabase
          .from('recent_transactions')
          .select('*')
          .order('timestamp', { ascending: false }) // Get the latest
          .limit(TRANSACTIONS_LIMIT);

        if (!mounted) return;

        if (dbError) {
          console.error('Error fetching recent transactions from Supabase:', dbError);
          setError(new Error('Failed to fetch recent transactions: ' + dbError.message));
          setStats(analyzeTransactions([])); // Analyze empty array to get default/empty stats
        } else if (dbTxs) {
          const transactions = dbTxs.map(transformDbTxToTransaction);
          setStats(analyzeTransactions(transactions));
        } else {
          setStats(analyzeTransactions([])); // No data found
        }
      } catch (err) {
        if (!mounted) return;
        console.error('Catch block error fetching/analyzing transactions:', err);
        setError(err instanceof Error ? err : new Error('An unexpected error occurred'));
        setStats(analyzeTransactions([]));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchAndAnalyzeTransactions(); // Initial fetch

    const channel = supabase
      .channel('public:recent_transactions')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'recent_transactions' },
        (payload) => {
          if (!mounted) return;
          console.log('Realtime update received for recent_transactions:', payload);
          // Re-fetch and re-analyze on any change to the table to get the latest set
          fetchAndAnalyzeTransactions();
        }
      )
      .subscribe((status, err) => {
        if (!mounted) return;
        if (err) {
          console.error('Realtime subscription error for recent_transactions:', err);
          // setError(new Error('Realtime connection failed for recent_transactions: ' + err.message)); // This might be too noisy
        } else if (status === 'SUBSCRIBED') {
          console.log('Successfully subscribed to recent_transactions updates.');
        }
      });

    return () => {
      mounted = false;
      if (channel) {
        supabase.removeChannel(channel).catch(errRm => console.error("Failed to remove channel for recent_transactions", errRm));
      }
    };
  }, []);

  return { stats, loading, error };
}